<template>
  <div
    class="selection-wheel"
    :class="{running, 'prevent-blur-on-desktop': preventBlurOnDesktop, 'pointer-cursor': pointerCursor}"
    @click="onClickOnContainer"
    >
    <!-- <canvas ref="canvas" class="canvas" /> -->
    <canvas
      :width="width"
      :height="height"
      ref="canvas"
      class="canvas"
      @mousedown.prevent="onMouseDown"
      @mousemove.prevent="onMouseMove"
      @mouseup.prevent="onMouseUp"
      @touchstart="onMouseDown"
      @touchmove.prevent="onMouseMove"
      @touchend="onMouseUp"
      @mousewheel="onMouseWheel"
      @DOMMouseScroll="onMouseWheel"
      />
<!--     <transition>
      <MobileDragHintWheel v-if="hintVisible" />
    </transition> -->
    <div class="fader" :class="{'done': forMore}" />
  </div>
</template>

<script>
import find from 'lodash/find'
import shuffle from 'lodash/shuffle'
import Viewport from '@/mixins/Viewport'
import RAF from '@/utils/RAF'
import ElasticNumber from '@/utils/ElasticNumber'
import normalizeWheel from '@/utils/normalizeWheel'
import sortBy from 'lodash/sortBy'
import defer from 'lodash/defer'
/* global THREE */

// cache for text measument width
const textMeasurementCache = {}
const textBitmaps = {}

export default {
  name: 'ConceptsSelectionWheel',

  mixins: [Viewport],

  props: {
    concepts: {
      type: Array,
      required: true
    },
    selected: {
      type: Array,
      required: false
    },
    positions: {
      type: Array,
      required: true
    },
    maxSelection: {
      type: Number,
      required: false,
      default: 10
    },
    preventBlurOnDesktop: {
      type: Boolean,
      default: false
    },
    inactivityTime: {
      type: Number,
      required: false
    },
    forMore: {
      type: Boolean,
      required: false
    }
  },

  data: function () {
    return {
      radii: [],
      angles: [],
      wheelRotation: new ElasticNumber(0),
      isMouseDown: false,
      overConceptWord: null,
      mouse: new THREE.Vector2(),
      mouseDown: new THREE.Vector2(),
      mouseDownT: 0,
      mouseDownWheelRot: 0,
      fontBase: 1680,
      fontSize: 14
      // hintVisible: false
    }
  },

  mounted: function () {
    this._clock = new THREE.Clock()
    this._conceptWords = []
    this.onConceptsChange(this.concepts)
    this._tempPositionObject = {}
    this._ctx = this.$refs.canvas.getContext('2d')
    this._lastInteractionTime = this._clock.elapsedTime

    RAF.add(this.render)
  },

  beforeDestroy: function () {
    RAF.remove(this.render)
  },

  methods: {
    onClickOnContainer: function (event) {
      if (!this.running) {
        let sel = this.selected.concat()
        sel.splice(-1, 1)
        this.$emit('update:selected', sel)
      }
    },
    render: function () {
      let delta = this._clock.getDelta()
      if (this.running && !this.isMouseDown) {
        this.wheelRotation.target -= 0.001 // 0.0015
      }
      this.wheelRotation.update(delta)

      let positions = []
      for (let i = 0; i < this.concepts.length; i++) {
        let concept = this.concepts[i]
        if (this.conceptIsSelected(concept)) {
          // position is based on index so we need to loop all concepts
          this.conceptLabelPosition(i, this._tempPositionObject, 0, true)
          let concPos = this._internalConceptPositions[i]
          concPos.x = (this._tempPositionObject.x / this.width) * 2 - 1
          concPos.y = (this._tempPositionObject.y / this.height) * 2 - 1
          concPos.t = this._tempPositionObject.t
          concPos.r = this._tempPositionObject.r
          positions.push(concPos)
        }
      }
      this.drawCanvas(delta)

      if (!this.running) {
        this._lastInteractionTime = this._clock.elapsedTime
        // this.hintVisible = false
      }

      this.$emit('update:inactivityTime', this._clock.elapsedTime - this._lastInteractionTime)

      // if (this._clock.elapsedTime - this._lastInteractionTime > 10 && this.$root.platform.mobile) {
      //   this.hintVisible = true
      // }

      this.$emit('update:positions', positions)
    },

    drawCanvas (delta) {
      let ctx = this._ctx

      ctx.clearRect(0, 0, this.width, this.height)

      let overConceptWords = []

      for (let i = 0; i < this._conceptWords.length; i++) {
        this.conceptLabelPosition(i, this._tempPositionObject, 0, true)

        let conceptWord = this._conceptWords[i]
        conceptWord.selected = this.conceptIsSelected(this.concepts[i])
        conceptWord.enabled = !(!conceptWord.selected && this.selected.length >= this.maxSelection)

        conceptWord.draw(delta, ctx, this._tempPositionObject.cx, this._tempPositionObject.cy, this._tempPositionObject.t, this._tempPositionObject.r)

        if (conceptWord.isOnScreen) {
          let hitTest = this._conceptWords[i].hitTest(this.mouse)
          if (hitTest !== null) {
            overConceptWords.push({word: this._conceptWords[i], distance: hitTest})
          }
        }
      }
      if (overConceptWords.length > 0) {
        this.overConceptWord = sortBy(overConceptWords, ['distance'])[0].word
      } else {
        this.overConceptWord = null
      }
    },

    onConceptClick (concept) {
      let sel = this.selected.concat()
      if (this.conceptIsSelected(concept)) {
        // if concept is already selected then remove it from selection
        concept.confirmed = false
        // push it at the end of the selection
        let index = sel.indexOf(concept)
        if (index !== -1) {
          sel.splice(index, 1)
          sel.push(concept)
        }
      } else {
        // concept is not selected = add it to selection
        sel.push(concept)
      }
      this.$emit('update:selected', sel)
    },

    conceptIsSelected (concept) {
      return find(this.selected, {label: concept.label}) !== undefined
    },
    conceptIsConfirmed (concept) {
      return concept.confirmed
    },
    conceptLabelPosition (index, targetObject = null, radiusOffset = 0, includeWheelRotation = false) {
      let x = this.width / 2
      // add (this.viewPort.height / 2) to make circle center below screen
      let y = this.height * 2
      let r = (this.radii[index] * y) + radiusOffset

      let wheelRotation = includeWheelRotation ? this.wheelRotation.value + Math.PI / 2 : 0

      let t = this.angles[index] + wheelRotation

      let posX = x + (r * Math.cos(t))
      let posY = y + (r * Math.sin(t))

      if (targetObject) {
        // write to targetObject to save memory
        // if required
        targetObject.x = posX
        targetObject.y = posY
        targetObject.t = t
        targetObject.r = r
        targetObject.cx = x
        targetObject.cy = y
        return
      }

      return {
        x: posX,
        y: posY,
        t: t,
        r: r,
        cx: x,
        cy: y
      }
    },

    generateRandomPlacement () {
      if (this.radii.length > 0 || this.angles.length > 0) {
        return
      }
      this.radii = []
      this.angles = []
      // this.randRadii.seed('dasdasdsa') // initialize random number generator
      // this.randAngles.seed('sax<dxcsda34asef') // initialize random number generator
      // minimum radius (0 - 1)
      const minrad = 0.65
      // maximum radius (0 - 1)
      const maxrad = 0.95
      // number of radii to subdivide our circle into
      // less == more sparse word cloud
      // more == more randomized (but closer)
      const numradii = 6
      // more angles = more close to each other words
      const numAnglesPerRadius = 5

      let radiiAngles = []
      let radStep = (maxrad - minrad) / numradii

      for (let r = 0; r < numradii; r++) {
        let startAngle = Math.random() * (Math.PI * 2)
        let rad = minrad + (r * radStep)
        let nA = Math.round(numAnglesPerRadius + (r * Math.PI)) // more radius = more angles
        for (let t = 0; t < nA; t++) {
          radiiAngles.push({
            angle: startAngle + (t / nA) * (Math.PI * 2),
            radius: rad
          })
        }
      }
      // number of concepts we can accomodate
      console.log('max number of concepts wheel can accomodate', radiiAngles.length)
      console.log('number of concepts', this.concepts.length)
      radiiAngles = shuffle(radiiAngles)

      this.concepts.forEach((concept, index) => {
        this.radii.push(radiiAngles[index].radius)
        this.angles.push(radiiAngles[index].angle)
      })
    },

    onMouseDown: function (event) {
      let x = event.touches && event.touches.length > 0 ? event.touches[0].clientX : event.clientX
      let y = event.touches && event.touches.length > 0 ? event.touches[0].clientY : event.clientY

      x *= window.devicePixelRatio
      y *= window.devicePixelRatio

      this.mouse.set(x, y)
      this.mouseDown.set(x, y)

      this._lastInteractionTime = this._clock.elapsedTime

      x = (this.width / 2) - x
      y = (this.height) - y
      this.mouseDownT = Math.atan2(x, y)
      this.isMouseDown = true
      this.mouseDownWheelRot = this.wheelRotation.value
    },
    onMouseUp: function (event) {
      this.isMouseDown = false

      this._lastInteractionTime = this._clock.elapsedTime
      if (this.overConceptWord && Math.abs(this.mouseDown.distanceTo(this.mouse)) < 20) {
        defer(this.onConceptClick, this.overConceptWord.concept)
        // this.onConceptClick(this.overConceptWord.concept)
        // this.hintVisible = false
      } else if (this.$root.platform.mobile) {
        if (Math.abs(this.mouseDown.distanceTo(this.mouse) < 20)) {
          // this is a tap on nothing
          // this.hintVisible = true
        }
      }
    },
    onMouseMove: function (event) {
      let x = event.touches && event.touches.length > 0 ? event.touches[0].clientX : event.clientX
      let y = event.touches && event.touches.length > 0 ? event.touches[0].clientY : event.clientY

      x *= window.devicePixelRatio
      y *= window.devicePixelRatio
      this.mouse.set(x, y)

      // this.hintVisible = false
      if (this.isMouseDown) {
        this._lastInteractionTime = this._clock.elapsedTime
        x = (this.width / 2) - x
        y = (this.height) - y
        let deltaT = this.mouseDownT - Math.atan2(x, y)
        this.wheelRotation.target = this.mouseDownWheelRot + (deltaT * 2)
      }
    },
    onMouseWheel: function (event) {
      let normalized = normalizeWheel(event)
      this._lastInteractionTime = this._clock.elapsedTime
      event.preventDefault()
      // console.log(normalized.pixelY, normalized.pixelX)
      this.wheelRotation.target -= (normalized.pixelY + normalized.pixelX) * 0.001
    },
    onConceptsChange: function (value) {
      this._internalConceptPositions = value.map(concept => {
        return {
          x: 0,
          y: 0,
          concept
        }
      })
      this.generateRandomPlacement()

      this._conceptWords = value.map(concept => {
        return new ConceptWord(concept, {width: this.width, height: this.height}, window.devicePixelRatio)
      })
    }
  },

  computed: {
    width: function () {
      return this.viewPort.width * window.devicePixelRatio
    },
    height: function () {
      return this.viewPort.height * window.devicePixelRatio
    },
    pointerCursor: function () {
      return this.overConceptWord !== null
    },
    running: function () {
      let running = true
      this.selected.some((concept) => {
        if (!concept.confirmed) {
          running = false
          return true
        }
      })
      return running
    }
  },

  watch: {
    viewPort: function (value) {
      this._conceptWords.forEach(word => {
        word.viewPort = {width: this.width, height: this.height}
      })
    },
    concepts: function (value) {
      // console.log('concepts change')
      this.onConceptsChange(value)
    }
  }
}

class ConceptWord {
  constructor (concept, viewPort, pixelRatio) {
    this.concept = concept
    this.pixelRatio = pixelRatio
    this.normalFontSize = 14 * pixelRatio
    this.fontSize = this.normalFontSize
    this.selectedFontSize = 24 * pixelRatio

    this.normalFontColor = new THREE.Color('rgb(111,111,110)')
    this.selectedFontColor = new THREE.Color(concept.categoryObject.color)

    this.selectedAlpha = new ElasticNumber(0)
    this.selectedAlpha.speed = 10

    this.label = this.concept.label.toUpperCase()
    this.bounds = new THREE.Box2()
    this.screenBounds = new THREE.Box2()
    this.selected = false
    this.enabled = true
    this.cachedAngles = {}
    this.cachedCumulativeAngle = []
    this.viewPort = viewPort
  }

  draw (delta, ctx, x, y, t, r) {
    // ctx.clearRect(this.bounds.min.x, this.bounds.min.y, this.bounds.max.x - this.bounds.min.x, this.bounds.max.y - this.bounds.min.y)

    this.selectedAlpha.target = this.selected ? 1 : 0
    this.selectedAlpha.update(delta)

    // this.fontSize = Math.round(this.lerpNumber(this.normalFontSize, this.selectedFontSize, this.selectedAlpha.value) * 1) / 1
    this.fontSize = this.lerpNumber(this.normalFontSize, this.selectedFontSize, this.selectedAlpha.value)
    let font = '600 ' + this.fontSize + 'px Muli'
    // let font = (this.selected ? '600' : '700') + ' ' + this.fontSize + 'px Muli'
    ctx.font = font
    // let fillStyle = '#' + this.normalFontColor.clone().lerp(this.selectedFontColor, this.selectedAlpha.value).getHexString()
    let fillStyle = this.selected ? '#' + this.selectedFontColor.getHexString() : '#' + this.normalFontColor.getHexString()
    ctx.fillStyle = fillStyle

    let angles = []
    let cumulativeAngle = 0
    let lastWidth = 0

    // first pass determine angles
    for (let i = 0; i < this.label.length; i++) {
      let char = this.label.charAt(i)
      // let width = ctx.measureText(char).width

      // use cached widths
      let width
      if (!textMeasurementCache[char + '-' + font]) {
        width = ctx.measureText(char).width
        textMeasurementCache[char + '-' + font] = width
      } else {
        width = textMeasurementCache[char + '-' + font]
      }
      angles.push(t + cumulativeAngle)
      cumulativeAngle += 2 * Math.asin(width / (r * 2))
      if (i === this.label.length - 1) lastWidth = width
    }
    let startAngle = -(cumulativeAngle / 2)

    // second pass determine bounds + positions
    let minX = Number.POSITIVE_INFINITY
    let minY = Number.POSITIVE_INFINITY
    let maxX = -Number.POSITIVE_INFINITY
    let maxY = -Number.POSITIVE_INFINITY
    let posXArray = []
    let posYArray = []
    this.screenBounds.min.set(0, 0)
    this.screenBounds.max.set(this.viewPort.width, this.viewPort.height)

    let margin = 10 * this.pixelRatio

    for (let i = 0; i < this.label.length; i++) {
      let t = startAngle + angles[i]
      let posX = posXArray[i] = x + (r * Math.cos(t))
      let posY = posYArray[i] = y + (r * Math.sin(t))
      if (posX + margin < 0 || posY + margin < 0 || posX > this.viewPort.width || posY > this.viewPort.height) continue
      minX = Math.min(posX, minX)
      minY = Math.min(posY - this.fontSize, minY)
      maxX = Math.max(posX + lastWidth, maxX)
      maxY = Math.max(posY, maxY)
    }
    this.bounds.min.set(minX, minY)
    this.bounds.max.set(maxX, maxY)
    this.bounds.expandByScalar(20)

    // 3rd pass draw
    if (this.screenBounds.intersectsBox(this.bounds) && !this.concept.confirmed) {
      for (let i = 0; i < this.label.length; i++) {
        let t = startAngle + angles[i]
        let posX = posXArray[i]
        let posY = posYArray[i]
        if (posX + margin < 0 || posY + margin < 0 || posX > this.viewPort.width || posY > this.viewPort.height) continue
        ctx.save()

        let rotation = t + (Math.PI / 2)
        ctx.globalAlpha = this.enabled ? 1 : 0.5
        // ctx.translate(posX, posY)
        ctx.translate(posX, posY - this.fontSize)
        ctx.rotate(rotation)
        // ctx.fillText(this.label.charAt(i), 0, 0)

        let bmp = this.getLetterBitmap(this.label.charAt(i), '600 ' + this.selectedFontSize + 'px Muli', fillStyle)

        // bmp.width : selectedFontSize = x : fontSize
        let w = (bmp.width * this.fontSize) / this.selectedFontSize
        let h = (bmp.height * this.fontSize) / this.selectedFontSize
        ctx.drawImage(bmp, 0, 0, bmp.width, bmp.height, 0, 0, w, h)

        ctx.restore()
      }
      // ctx.strokeRect(this.bounds.min.x, this.bounds.min.y, this.bounds.max.x - this.bounds.min.x, this.bounds.max.y - this.bounds.min.y)
    }
  }

  getLetterBitmap (char, font, fillStyle) {
    if (!textBitmaps[char + '-' + font + '-' + fillStyle]) {
      // console.log('create new text bitmap', char, font, fillStyle)
      let canvas = textBitmaps[char + '-' + font + '-' + fillStyle] = document.createElement('canvas')
      let ctx = canvas.getContext('2d')
      let width
      ctx.font = font
      if (!textMeasurementCache[char + '-' + font]) {
        width = ctx.measureText(char).width
        textMeasurementCache[char + '-' + font] = width
      } else {
        width = textMeasurementCache[char + '-' + font]
      }
      let height = this.selectedFontSize
      canvas.width = width
      canvas.height = height
      ctx.font = font
      ctx.fillStyle = fillStyle
      ctx.fillText(char, 0, height * 0.85)
    }
    return textBitmaps[char + '-' + font + '-' + fillStyle]
  }

  lerpNumber (v0, v1, t) {
    return v0 + t * (v1 - v0)
  }
  get isOnScreen () {
    return this.screenBounds.intersectsBox(this.bounds)
  }
  hitTest (v) {
    if (this.enabled && this.bounds.containsPoint(v)) {
      return Math.abs(this.bounds.getCenter().distanceTo(v))
    }
    return null
  }
}
</script>

<style scoped lang="scss">
@import 'variables';



@import 'mixins';

.selection-wheel {
  filter: blur(10px);
  z-index: $z-background;
  -webkit-tap-highlight-color: transparent;
  user-select: none;

  .canvas {
    position: absolute;
    pointer-events: none;
    width: 100%;
    height: 100%;
  }

  &.running {
    filter: none;
    pointer-events: all;
    z-index: $z-foreground;

    .canvas {
      pointer-events: all;
    }
  }

  @include breakpoint(#{$bp-sm}px) {
    &.prevent-blur-on-desktop {
      filter: none;
    }
  }

  &.pointer-cursor {
    cursor: pointer;
  }
}

.fader {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 150px;
  display: none;
  // background: linear-gradient(to bottom, color-alpha($c-background, 0), $c-background, $c-background, $c-background);
  pointer-events: none;

  background-color: #f9f8f7;
  box-shadow: 0 -7px 17px -13px #909090;
  border-top: 1px solid #f9f8f7;

  @include breakpoint(#{$bp-sm}px) {
    display: block;
  }

  transition: box-shadow 0.5s $ease-in-out-quad;

  &.done {
    background: linear-gradient(to bottom, color-alpha($c-background, 0), $c-background, $c-background, $c-background);
    box-shadow: none;
    /* stylelint-disable */
    border-top: none;
    /* stylelint-enable */
  }
}


</style>



// WEBPACK FOOTER //
// src/molecules/CanvasConceptsSelectionWheel.vue